import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useSearch } from '@tanstack/react-router'
import { ArrowDown, ArrowLeft, ArrowUp, Minus } from 'lucide-react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { z } from 'zod'
import {
  type CoachPeriodFilter,
  type CoachPeriodStats,
  type CoachSellerConsultationRow,
  type CoachSellerDetail,
  type CoachTopTag,
  getCoachSellerDetail,
} from '@/api/coach'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { describeErrorMessage } from '@/lib/errors'
import { cn } from '@/lib/utils'

const searchSchema = z.object({
  period: z.enum(['week', 'month']).optional(),
  storeId: z.string().optional(),
})

export const Route = createFileRoute('/coach/sellers/$id')({
  // ValidateSearch сохраняет фильтры между переходами назад на сводку
  // магазина — пользователь видит тот же период без повторного клика.
  validateSearch: searchSchema,
  component: CoachSellerDetailPage,
})

type PresetPeriod = CoachPeriodFilter['kind']

const PERIOD_LABEL: Record<PresetPeriod, string> = {
  week: 'Неделя',
  month: 'Месяц',
}

function CoachSellerDetailPage() {
  const { id: sellerId } = Route.useParams()
  const search = useSearch({ from: '/coach/sellers/$id' })
  const period: PresetPeriod = search.period ?? 'week'
  const navigate = Route.useNavigate()

  const query = useQuery({
    queryKey: ['coach', 'seller', sellerId, period],
    queryFn: () => getCoachSellerDetail(sellerId, { kind: period }),
  })

  const backTo = search.storeId
    ? { to: '/coach/stores/$id' as const, params: { id: search.storeId } }
    : { to: '/coach/stores' as const }

  return (
    <main className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-6 pb-12">
      <Link
        {...backTo}
        className="-ml-3 inline-flex w-fit items-center gap-2 rounded-md px-3 py-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {search.storeId ? 'К сводке магазина' : 'К списку магазинов'}
      </Link>

      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            {query.data?.seller.fullName ?? 'Продавец'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {query.data?.store
              ? `${query.data.store.name}, ${query.data.store.city}`
              : 'Детали продавца за период.'}
          </p>
        </div>
        <PeriodToggle
          value={period}
          onChange={(next) =>
            // navigate с частичным search сохраняет storeId, обновляя
            // только period — так пользователь не теряет контекст.
            navigate({ search: (prev) => ({ ...prev, period: next }) })
          }
        />
      </header>

      {query.isLoading ? (
        <StatusCard title="Загружаем данные продавца" description="Это займёт секунду." />
      ) : query.isError ? (
        <StatusCard
          title="Не удалось загрузить продавца"
          description={describeErrorMessage(query.error, 'Попробуйте обновить.')}
        />
      ) : query.data ? (
        <SellerBody
          detail={query.data}
          sellerId={sellerId}
          period={period}
          storeId={search.storeId}
        />
      ) : null}
    </main>
  )
}

interface PeriodToggleProps {
  value: PresetPeriod
  onChange: (next: PresetPeriod) => void
}

function PeriodToggle({ value, onChange }: PeriodToggleProps) {
  return (
    <div
      role="tablist"
      aria-label="Период продавца"
      className="inline-flex items-center rounded-lg border bg-muted p-1"
    >
      {(['week', 'month'] as PresetPeriod[]).map((kind) => {
        const active = kind === value
        return (
          <button
            key={kind}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(kind)}
            className={cn(
              'rounded-md px-3 py-1 text-sm font-medium transition-colors',
              active
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {PERIOD_LABEL[kind]}
          </button>
        )
      })}
    </div>
  )
}

interface SellerBodyProps {
  detail: CoachSellerDetail
  sellerId: string
  period: PresetPeriod
  storeId?: string
}

function SellerBody({ detail, sellerId, period, storeId }: SellerBodyProps) {
  const countDelta = detail.current.consultationsCount - detail.previous.consultationsCount
  const scoreDelta = diffOrNull(detail.current.avgScorePercent, detail.previous.avgScorePercent)
  const topCurrent = detail.current.tagDistribution[0]
  const topPrevious = detail.previous.tagDistribution.find((t) => t.code === topCurrent?.code)
  const topDelta = topCurrent && topPrevious ? topCurrent.count - topPrevious.count : null

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiCard
          title="Консультаций"
          value={detail.current.consultationsCount.toString()}
          delta={countDelta}
          deltaSuffix=""
          hint={`За прошлый период: ${detail.previous.consultationsCount}`}
        />
        <KpiCard
          title="Средний балл"
          value={formatScore(detail.current.avgScorePercent)}
          delta={scoreDelta}
          deltaSuffix="%"
          hint={
            detail.previous.avgScorePercent === null
              ? 'За прошлый период нет данных'
              : `За прошлый период: ${formatScore(detail.previous.avgScorePercent)}`
          }
          emptyHint={
            detail.current.avgScorePercent === null ? 'Пока нет обработанных записей' : undefined
          }
        />
        <KpiCard
          title="Топ-тег"
          value={topCurrent ? topCurrent.label : '—'}
          delta={topDelta}
          deltaSuffix=""
          hint={topCurrent ? `Встретился ${topCurrent.count} раз` : 'Пока нет тегов за период'}
        />
      </div>

      <TagDistributionCard current={detail.current} />
      <ConsultationsCard
        consultations={detail.consultations}
        sellerId={sellerId}
        period={period}
        storeId={storeId}
      />
      <RecommendationsCard bullets={detail.aggregatedRecommendations} />
    </div>
  )
}

interface KpiCardProps {
  title: string
  value: string
  delta: number | null
  deltaSuffix: string
  hint: string
  emptyHint?: string
}

function KpiCard({ title, value, delta, deltaSuffix, hint, emptyHint }: KpiCardProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <div className="flex items-baseline gap-2">
          <CardTitle className="text-3xl leading-tight">{value}</CardTitle>
          {delta !== null ? <DeltaBadge delta={delta} suffix={deltaSuffix} /> : null}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-xs text-muted-foreground">{emptyHint ?? hint}</p>
      </CardContent>
    </Card>
  )
}

function DeltaBadge({ delta, suffix }: { delta: number; suffix: string }) {
  const rounded = Math.round(delta * 10) / 10
  if (rounded === 0) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
        role="status"
        aria-label="Без изменений"
      >
        <Minus className="h-3 w-3" aria-hidden="true" />0{suffix}
      </span>
    )
  }
  const positive = rounded > 0
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        positive ? 'bg-emerald-500/10 text-emerald-600' : 'bg-destructive/10 text-destructive',
      )}
      role="status"
      aria-label={`Изменение: ${positive ? '+' : ''}${rounded}${suffix}`}
    >
      {positive ? (
        <ArrowUp className="h-3 w-3" aria-hidden="true" />
      ) : (
        <ArrowDown className="h-3 w-3" aria-hidden="true" />
      )}
      {positive ? '+' : ''}
      {rounded}
      {suffix}
    </span>
  )
}

function TagDistributionCard({ current }: { current: CoachPeriodStats }) {
  if (current.tagDistribution.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Распределение тегов</CardTitle>
          <CardDescription>Пока нет размеченных консультаций за период.</CardDescription>
        </CardHeader>
      </Card>
    )
  }
  const data = current.tagDistribution.map((t: CoachTopTag) => ({ label: t.label, count: t.count }))
  return (
    <Card>
      <CardHeader>
        <CardTitle>Распределение тегов</CardTitle>
        <CardDescription>Как часто продавец использовал каждое действие.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-64 w-full" role="img" aria-label="Распределение тегов">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 24 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="color-mix(in oklch, var(--color-muted-foreground) 15%, transparent)"
              />
              <XAxis
                dataKey="label"
                stroke="var(--color-muted-foreground)"
                fontSize={11}
                interval={0}
                angle={-20}
                dy={12}
                height={60}
                textAnchor="end"
              />
              <YAxis allowDecimals={false} stroke="var(--color-muted-foreground)" fontSize={11} />
              <Tooltip
                cursor={{ fill: 'color-mix(in oklch, var(--color-muted) 50%, transparent)' }}
                contentStyle={{
                  background: 'var(--color-background)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 6,
                  fontSize: 12,
                }}
              />
              <Bar
                dataKey="count"
                fill="var(--color-primary)"
                radius={[6, 6, 0, 0]}
                maxBarSize={48}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}

interface ConsultationsCardProps {
  consultations: CoachSellerConsultationRow[]
  sellerId: string
  period: PresetPeriod
  storeId?: string
}

const STATUS_LABEL: Record<CoachSellerConsultationRow['status'], string> = {
  uploaded: 'Загружена',
  queued: 'В очереди',
  transcribing: 'Расшифровка',
  analyzing: 'Анализ',
  done: 'Готово',
  failed: 'Ошибка',
}

function ConsultationsCard({ consultations, sellerId, period, storeId }: ConsultationsCardProps) {
  if (consultations.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Консультации за период</CardTitle>
          <CardDescription>Продавец пока не загрузил ни одной записи за период.</CardDescription>
        </CardHeader>
      </Card>
    )
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Консультации за период</CardTitle>
        <CardDescription>Клик на консультацию — карточка с расшифровкой и оценкой.</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <ul className="divide-y" aria-label="Список консультаций продавца">
          {consultations.map((c) => (
            <li key={c.id}>
              <Link
                to="/coach/consultations/$id"
                params={{ id: c.id }}
                search={{ sellerId, period, storeId }}
                className="flex flex-col gap-1 px-4 py-3 transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium text-foreground">
                    {formatDate(c.consultationDate)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Загружена {formatDateTime(c.uploadedAt)}
                    {typeof c.audioDurationSec === 'number'
                      ? ` · ${formatDuration(c.audioDurationSec)}`
                      : ''}
                  </span>
                </div>
                <StatusBadge status={c.status} />
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

function StatusBadge({ status }: { status: CoachSellerConsultationRow['status'] }) {
  const tone =
    status === 'done'
      ? 'bg-emerald-500/10 text-emerald-600'
      : status === 'failed'
        ? 'bg-destructive/10 text-destructive'
        : 'bg-muted text-muted-foreground'
  return (
    <span
      className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', tone)}
    >
      {STATUS_LABEL[status]}
    </span>
  )
}

function RecommendationsCard({ bullets }: { bullets: string[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Зоны роста за период</CardTitle>
        <CardDescription>
          {bullets.length > 0
            ? 'Сформированы на основе последних консультаций продавца.'
            : 'Появятся, когда за период будет хотя бы три обработанные записи.'}
        </CardDescription>
      </CardHeader>
      {bullets.length > 0 ? (
        <CardContent>
          <ul className="flex list-disc flex-col gap-2 pl-5 text-sm text-foreground">
            {bullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>
        </CardContent>
      ) : null}
    </Card>
  )
}

function StatusCard({ title, description }: { title: string; description: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
    </Card>
  )
}

function formatScore(value: number | null): string {
  if (value === null) return '—'
  return `${Math.round(value)}%`
}

function diffOrNull(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null) return null
  return current - previous
}

function formatDate(iso: string): string {
  // API отдаёт YYYY-MM-DD — форматируем в DD.MM.YYYY для читаемости тренеру.
  const [y, m, d] = iso.split('-')
  if (!y || !m || !d) return iso
  return `${d}.${m}.${y}`
}

function formatDateTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds} с`
  const m = Math.round(seconds / 60)
  return `${m} мин`
}
